<template>
  <ReportWrapper>
    <template #actions>
      <div class="flex items-start gap-x-2">
        <div class="flex flex-wrap gap-x-2">
          <CfsSelect
            v-model="selectedPeriodType"
            placeholder="Choose period"
            :items="DatePeriodsOptions"
            class="max-w-[140px] x-sm:w-full x-sm:max-w-full"
            :rules="[rules.required]"
            @change="onChangePeriodType"
          />

          <CfsDateField
            v-if="selectedPeriodType === DatePeriods.WEEKLY"
            v-model="selectedDate"
            :mode="dateMode"
            timezone=""
            range
            class="w-2/6 x-sm:w-full"
            :rules="[rules.required]"
            :available-dates="{ weekdays: [1, 7] }"
          />
          <CfsDateField
            v-else-if="selectedPeriodType === DatePeriods.DAILY"
            v-model="selectedSingleDate"
            :mode="dateMode"
            timezone=""
            class="w-2/6 x-sm:w-full"
            :rules="[rules.required]"
          />
          <v-menu v-else :close-on-content-click="false" nudge-right="10" offset-x :max-width="300">
            <template #activator="{ on, attrs }">
              <div class="relative w-2/6 x-sm:w-full" v-bind="attrs" v-on="on">
                <CfsTextField
                  placeholder="Select month"
                  label=""
                  hide-details
                  :value="periodDisplayValue"
                />
                <div
                  v-if="selectedPeriod"
                  class="top-0 right-[10px] absolute z-100 h-full cursor-pointer"
                >
                  <v-icon
                    class="absolute top-[8px] cursor-pointer"
                    color="waterloo"
                    @click.stop="onChangePeriod()"
                  >
                    mdi-close
                  </v-icon>
                </div>
              </div>
            </template>
            <v-date-picker
              :value="periodDisplayValue"
              color="primary"
              type="month"
              @change="onChangePeriod"
            />
          </v-menu>
          <CustomerAutocomplete
            v-if="selectedPeriodType === DatePeriods.DAILY"
            label=""
            placeholder="Customer"
            customer
            :readonly="readonlyRef"
            :rules="[rules.required]"
            :value="formData.customer"
            @change="onChangeCustomer"
          />
          <CfsSelect
            v-if="selectedPeriodType === DatePeriods.DAILY"
            v-model="selectedWarehouseCustomer"
            :items="warehouseCustomers"
            :loading="isLoadingWarehouseCustomers"
            :disabled="!formData.customer || !selectedSingleDate"
            class="customer-select w-1/4 x-sm:w-full"
            label=""
            placeholder="Rate"
            item-text="chargeDescription"
            hide-details
            multiple
            :return-object="true"
          >
            <template #selection="{ item }">
              <span class="mr-2">{{ item?.chargeDescription }}</span>
              <IconFulfilled v-if="item?.isFinalized" width="16px" height="16px" color="#50B64D" />
              <v-tooltip v-else content-class="opacity--1 cfs-tooltip" color="#535353" top>
                <template #activator="{ on, attrs }">
                  <div>
                    <v-icon
                      color="gray"
                      size="16px"
                      v-bind="attrs"
                      class="mr-2 mt-[-4px]"
                      v-on="on"
                    >
                      mdi-timelapse
                    </v-icon>
                  </div>
                </template>
                <span>Pending</span>
              </v-tooltip>
            </template>
            <template #item="{ item }">
              <span class="mr-2">{{ item?.chargeDescription }}</span>
              <IconFulfilled v-if="item?.isFinalized" width="16px" height="16px" color="#50B64D" />
              <v-tooltip v-else content-class="opacity--1 cfs-tooltip" top>
                <template #activator="{ on, attrs }">
                  <div>
                    <v-icon
                      color="gray"
                      size="16px"
                      v-bind="attrs"
                      class="mr-2 mt-[-4px]"
                      v-on="on"
                    >
                      mdi-timelapse
                    </v-icon>
                  </div>
                </template>
                <span>Pending</span>
              </v-tooltip>
            </template>
          </CfsSelect>
          <CfsSelect
            v-else
            v-model="selectedCustomer"
            :items="customers"
            :is-loading="isFetchingCustomers"
            :disabled="!hasFetchedCustomers || isFetchingCustomers"
            class="customer-select w-2/6 x-sm:w-full"
            label=""
            placeholder="Customer"
            item-id="customer.accountNumber"
            item-value="customer.accountNumber"
            item-text="customer.accountName"
            hide-details
            :return-object="true"
            multiple
          >
            <template #prepend-item>
              <CfsCheckbox
                v-model="selectAllCustomers"
                :label="`${selectAllCustomers ? 'Deselect' : 'Select'} All`"
                class="h-full !pt-0 ml-4 mb-1"
                @change="onSelectAllCustomers"
              />
              <v-divider class="!border-[1px]"></v-divider>
            </template>
            <template #selection="{ item, index }">
              <template v-if="selectedCustomer?.length === 1">
                <span class="customer-name mr-2">{{ item?.customer?.accountName }}</span>
                <IconFulfilled v-if="item?.isFinalize" width="16px" height="16px" color="#50B64D" />
                <v-tooltip v-else content-class="opacity--1 cfs-tooltip" color="#535353" top>
                  <template #activator="{ on, attrs }">
                    <div>
                      <v-icon
                        color="gray"
                        size="16px"
                        v-bind="attrs"
                        class="mr-2 mt-[-4px]"
                        v-on="on"
                      >
                        mdi-timelapse
                      </v-icon>
                    </div>
                  </template>
                  <span>Pending</span>
                </v-tooltip>
              </template>
              <template v-else-if="index === 0 && selectedCustomer">
                <span class="chip-container">
                  <span class="chip">{{ item?.customer?.accountName }}</span>
                  <span class="others-count">(+{{ selectedCustomer.length - 1 }} OTHERS)</span>
                </span>
              </template>
            </template>
            <template #item="{ item }">
              <CfsCheckbox
                :input-value="
                  selectedCustomer?.some(
                    (c) => c.customer.accountNumber === item.customer.accountNumber
                  )
                "
                class="h-full"
              />
              <span class="customer-name mr-2">{{ item?.customer?.accountName }}</span>
              <IconFulfilled v-if="item?.isFinalize" width="16px" height="16px" color="#50B64D" />
              <v-tooltip v-else content-class="opacity--1 cfs-tooltip" top>
                <template #activator="{ on, attrs }">
                  <div>
                    <v-icon color="gray" size="16px" v-bind="attrs" class="mr-2 mt-[-4px]" v-on="on"
                      >mdi-timelapse</v-icon
                    >
                  </div>
                </template>
                <span>Pending</span>
              </v-tooltip>
            </template>
          </CfsSelect>
        </div>

        <div class="flex gap-x-2 grow-0">
          <v-btn
            color="primary"
            :disabled="isFinalizeDisabled"
            :loading="isFinalizingCourierCharges"
            @click="onFinalize"
          >
            Finalize
          </v-btn>
          <v-btn
            color="primary"
            :disabled="isDownloadDisabled"
            :loading="isDownloadingReport"
            @click="onDownloadReport"
          >
            <v-icon size="1rem" class="mr-1">mdi-tray-arrow-down</v-icon>
            <span>Download</span>
          </v-btn>
        </div>
      </div>
    </template>
  </ReportWrapper>
</template>

<script lang="ts">
import { computed, defineAsyncComponent, defineComponent, ref, watch, toRef } from 'vue';
import ReportWrapper from '@/components/reports/ReportWrapper.vue';
import { DatePeriods, DatePeriodsOptions } from '@/constants/reports';
import { useState } from '@/composables/common/useState';
import { DatePeriod } from '@/types/common/dateRange.model';
import { useReportCustomers } from '@/composables/reports/useReportCustomers';
import {
  GetCourierCustomersPayload,
  ReportCustomerEntity,
} from '@/services/reports/reports.customer.service';
import { useReports } from '@/composables/reports/useReports';
import { downloadFile } from '@/helpers/download';
import { AxiosError } from 'axios';
import { useToast } from '@/composables/useToast';
import dayjs from 'dayjs';
import IconFulfilled from '@/components/icons/IconFulfilled.vue';

// Add customer to existing type
type ExtendedPayload = GetCourierCustomersPayload & {
  customer?: string;
};

export default defineComponent({
  name: 'WarehouseReport',
  components: {
    IconFulfilled,
    ReportWrapper,
    CustomerAutocomplete: defineAsyncComponent(
      () => import('@/components/autocomplete/CustomerAutocompleteComponent.vue')
    ),
  },
  props: {
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    // Convert prop to ref to make it reactive
    const readonlyRef = toRef(props, 'readonly');

    const selectedPeriodType = ref<DatePeriods>(DatePeriods.MONTHLY);
    const selectedDate = ref<{ start: string; end: string } | undefined>(undefined);
    const selectedSingleDate = ref<string | undefined>(undefined);

    const dateMode = computed(() => {
      return selectedPeriodType.value === DatePeriods.MONTHLY ? 'month' : 'date';
    });

    const [selectedPeriod, setPeriod] = useState<DatePeriod | null>(null);

    const periodDisplayValue = computed(() =>
      selectedPeriod.value ? `${selectedPeriod.value.year}-${selectedPeriod.value.month}` : ''
    );

    const [selectedCustomer, setCustomer] = useState<ReportCustomerEntity[] | undefined>(undefined);

    const { useGetCourierCustomers } = useReportCustomers();

    const getCourierCustomersPayload = computed<ExtendedPayload>(() => {
      if (selectedPeriodType.value === DatePeriods.DAILY && selectedSingleDate.value) {
        return {
          frequency: DatePeriods.DAILY,
          day: dayjs.utc(selectedSingleDate.value).format('YYYY-MM-DD'),
          customer: selectedSingleDate.value,
        };
      }

      if (selectedPeriodType.value === DatePeriods.WEEKLY && selectedDate.value) {
        return {
          frequency: selectedPeriodType.value,
          period: {
            start: dayjs.utc(selectedDate.value.start).format('YYYY-MM-DD'),
            end: dayjs.utc(selectedDate.value.end).format('YYYY-MM-DD'),
          },
          customer: selectedDate.value.start,
        };
      }

      return {
        frequency: selectedPeriodType.value,
        ...(selectedPeriod.value ? { month: periodDisplayValue.value } : {}),
        customer:
          selectedPeriod.value?.year && selectedPeriod.value?.month
            ? `${selectedPeriod.value.year}-${selectedPeriod.value.month}`
            : undefined,
      };
    });

    const {
      data: customers,
      isLoading: isFetchingCustomers,
      isSuccess: hasFetchedCustomers,
      mutateAsync: refetchCustomers,
    } = useGetCourierCustomers();

    watch([periodDisplayValue, selectedDate, selectedSingleDate], () => {
      if (!periodDisplayValue.value && !selectedDate.value && !selectedSingleDate.value) return;

      // Skip API call in DAILY mode
      if (selectedPeriodType.value === DatePeriods.DAILY) return;

      refetchCustomers(getCourierCustomersPayload.value);
    });

    // Add new watch for customer changes in DAILY mode
    watch(
      () => formData.value.customer,
      (newCustomer) => {
        if (selectedPeriodType.value === DatePeriods.DAILY && newCustomer) {
          refetchCustomers(getCourierCustomersPayload.value);
        }
      }
    );

    const { useDownloadCourierReport, useFinalizeCourierReport } = useReports();

    const { mutateAsync: downloadReport, isLoading: isDownloadingReport } =
      useDownloadCourierReport();
    const { mutateAsync: finalizeCourierCharges, isLoading: isFinalizingCourierCharges } =
      useFinalizeCourierReport();

    const toast = useToast();

    const hasPendingCustomer = computed(() => {
      return selectedCustomer.value?.some((c) => c.isPending);
    });

    const onChangePeriod = (period?: string) => {
      if (!period) return setPeriod(null);
      const [year, month] = period?.split('-') ?? '';
      setPeriod({ year, month });
      setCustomer(undefined);
    };

    const onChangePeriodType = (periodType: DatePeriods) => {
      selectedPeriodType.value = periodType;
      setPeriod(null);
      selectedDate.value = undefined;
      selectedSingleDate.value = undefined;
      setCustomer(undefined);
      formData.value.customer = null;
    };

    const onDownloadReport = async () => {
      if (isDownloadingReport.value) return;
      try {
        const payload = getCourierCustomersPayload.value;
        const data = await downloadReport({
          ...payload,
          customer:
            selectedPeriodType.value === DatePeriods.DAILY
              ? [formData.value.customer?.accountNumber]
              : selectedCustomer.value?.map((c) => c.customer.accountNumber),
          ...(selectedPeriodType.value === DatePeriods.DAILY &&
          selectedWarehouseCustomer.value?.length
            ? { rateIds: selectedWarehouseCustomer.value.map((item) => item._id) }
            : {}),
        });

        if (!data) return;
        downloadFile(data.base64Data, data.filename ?? 'Courier Report');
      } catch (error) {
        const axiosError = error as AxiosError<string>;
        toast.info(axiosError?.response?.data!);
      }
    };

    const onFinalize = async () => {
      try {
        if (isFinalizingCourierCharges.value) return;

        const payload = getCourierCustomersPayload.value;
        await finalizeCourierCharges({
          ...payload,
          customer:
            selectedPeriodType.value === DatePeriods.DAILY
              ? [formData.value.customer?.accountNumber]
              : selectedCustomer.value?.map((c) => c.customer.accountNumber),
          ...(selectedPeriodType.value === DatePeriods.DAILY &&
          selectedWarehouseCustomer.value?.length
            ? { rateIds: selectedWarehouseCustomer.value.map((item) => item._id) }
            : {}),
        });

        toast.info('Courier charges finalization in progress...');
        await refetchCustomers(getCourierCustomersPayload.value);
      } catch (error) {
        const axiosError = error as AxiosError<string>;
        toast.info(axiosError?.response?.data!);
      }
    };

    const selectAllCustomers = computed(() => {
      return selectedCustomer.value?.length === customers.value?.length;
    });

    const onSelectAllCustomers = (value: boolean) => {
      if (value) {
        setCustomer(customers.value);
      } else {
        setCustomer(undefined);
      }
    };

    interface Customer {
      accountNumber: string;
      rateId?: string;
    }

    const formData = ref<{ customer: Customer | null }>({
      customer: null,
    });

    const onChangeCustomer = (customer: any) => {
      formData.value.customer = customer;
    };

    // Define validation rules as a reactive computed property
    const validationRules = computed(() => ({
      required: (value: any) => !!value || 'This field is required',
    }));

    // Add computed for button states in DAILY mode
    const isDailyModeValid = computed(() => {
      return formData.value.customer && selectedSingleDate.value;
    });

    // Modify button disabled conditions
    const isDownloadDisabled = computed(() => {
      if (selectedPeriodType.value === DatePeriods.DAILY) {
        return !isDailyModeValid.value || !selectedWarehouseCustomer.value;
      }
      return !selectedCustomer.value?.length;
    });

    const isFinalizeDisabled = computed(() => {
      if (selectedPeriodType.value === DatePeriods.DAILY) {
        return (
          !isDailyModeValid.value ||
          !selectedWarehouseCustomer.value ||
          selectedWarehouseCustomer.value.isFinalized
        );
      }
      return !selectedCustomer.value?.length || !hasPendingCustomer.value;
    });

    // Add new state and API call
    const { useGetCourierCustomers: useGetWarehouseCourierCustomers } = useReportCustomers();
    const {
      data: warehouseCustomers,
      isLoading: isLoadingWarehouseCustomers,
      mutateAsync: fetchWarehouseCustomers,
    } = useGetWarehouseCourierCustomers();

    // Modify watch for fetching warehouse customers
    watch(selectedSingleDate, (date) => {
      if (
        selectedPeriodType.value === DatePeriods.DAILY &&
        date &&
        formData.value.customer?.accountNumber
      ) {
        fetchWarehouseCustomers({
          frequency: DatePeriods.DAILY,
          day: dayjs.utc(date).format('YYYY-MM-DD'),
          customer: formData.value.customer.accountNumber,
        });
      }
    });

    // Add watch for customer changes to fetch warehouse customers
    watch(
      () => formData.value.customer,
      (customer) => {
        if (
          selectedPeriodType.value === DatePeriods.DAILY &&
          customer?.accountNumber &&
          selectedSingleDate.value
        ) {
          fetchWarehouseCustomers({
            frequency: DatePeriods.DAILY,
            day: dayjs.utc(selectedSingleDate.value).format('YYYY-MM-DD'),
            customer: customer.accountNumber,
          });
        }
      }
    );

    interface WarehouseCustomer {
      _id: string;
      chargeDescription: string;
      isFinalized: boolean;
    }

    const selectedWarehouseCustomer = ref<WarehouseCustomer[]>([]);

    return {
      selectedPeriodType,
      selectedPeriod,
      selectedDate,
      selectedSingleDate,
      DatePeriods,
      dateMode,
      DatePeriodsOptions,
      rules: validationRules,
      periodDisplayValue,
      onChangePeriod,
      onChangePeriodType,
      selectedCustomer,
      customers,
      hasPendingCustomer,
      isFetchingCustomers,
      hasFetchedCustomers,
      refetchCustomers,
      onDownloadReport,
      isDownloadingReport,
      isFinalizingCourierCharges,
      onFinalize,
      selectAllCustomers,
      onSelectAllCustomers,
      dayjs,
      formData,
      onChangeCustomer,
      readonlyRef,
      isDailyModeValid,
      isDownloadDisabled,
      isFinalizeDisabled,
      warehouseCustomers,
      isLoadingWarehouseCustomers,
      fetchWarehouseCustomers,
      selectedWarehouseCustomer,
    };
  },
});
</script>

<style lang="scss" scoped>
:deep(.customer-select .v-select__selections) {
  flex-wrap: nowrap !important;
  align-items: center !important;
  text-wrap: nowrap;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

:deep(.customer-select .v-icon.v-icon) {
  line-height: unset !important;
}

:deep(.customer-select .customer-name:not(:first-child)) {
  margin-left: 8px !important;
}

:deep(.v-menu__content .v-input__control) {
  margin-top: -4px !important;
}

.v-input--checkbox.v-input--dense {
  margin-top: 0px !important;
}

.chip-container {
  display: flex;
  align-items: center;
  gap: 4px;
}

.chip {
  background-color: #f1f5f9;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.875rem;
  max-width: 58%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.others-count {
  color: #94a3b8;
  font-size: 0.75rem;
}
</style>
